type Sprites = {
    urls: string[];
    src: string[];
    sprite: Record<string, [start: number, end: number]>;
}

const WWampTypes = {
    default: 'default',
    custom: 'custom',
} as const

type WWampType = keyof typeof WWampTypes;

type WWamp = {
    id: string;
    danger: number;
    volume: number;
    sound: string;
    type: WWampType;
    label: string;
}

const defaultSprites: Sprites = {
    urls: [
        "/audio/audio.pack.webm",
        "/audio/audio.pack.mp3"
    ],
    src: [
        "/audio/audio.pack.webm",
        "/audio/audio.pack.mp3",
    ],
    sprite: {
        "300": [0, 2536.0090702947846],
        "applause-lite": [4000, 3551.201814058957],
        "applause-more": [9000, 7540.861678004536],
        "awww": [18000, 1344.0136054421785],
        "bwamp-2": [21000, 2886.8707482993195],
        "bwamp-3": [25000, 2959.4104308390038],
        "bwamp": [29000, 3030.204081632654],
        "cha-ching": [34000, 1666.6666666666642],
        "cheers": [37000, 1655.9863945578215],
        "cowpaths": [40000, 2377.1428571428573],
        "drum-roll": [44000, 1388.6621315192756],
        "fire": [47000, 2196.9841269841268],
        "goose": [51000, 359.99999999999943],
        "ham-horn-multi": [53000, 2856.054421768704],
        "ham-horn": [57000, 1973.6961451247198],
        "horse": [60000, 2106.6666666666692],
        "lots-of-laughs": [64000, 2194.285714285712],
        "mindblown": [68000, 1524.9659863945624],
        "minecraft-death": [71000, 417.9591836734744],
        "no": [73000, 804.9659863945635],
        "nonono-cat": [75000, 3636.9841269841318],
        "oooh": [80000, 2545.6916099773252],
        "power-on": [84000, 4153.469387755109],
        "powerful": [90000, 612.0181405895693],
        "sad-trombone": [92000, 4017.052154195014],
        "twinkle": [98000, 3138.3673469387786],
        "yes": [103000, 924.9659863945539]
    }
}

function htmlToElement<T extends ChildNode>(html: string): T {
    const template = document.createElement('template');
    html = html.trim();
    template.innerHTML = html;
    if (template.content && template.content.firstChild) {
        return template.content.firstChild as T;
    } else if (template.firstChild) {
        return template.firstChild as T;
    } else {
        throw new Error('No child node found');
    }
}

function toElementId(wwamp: WWamp): string {
    return `${wwamp.sound}:${wwamp.id}`;
}

function getWwampElement(wwamp: WWamp): HTMLElement | null {
    const elId = toElementId(wwamp);
    return document.getElementById(elId);
}

const dangerColors = ["dark-green", "orange", "dark-red"];

function wwampFromElement(el: HTMLElement): WWamp | null {
    const volume = el.dataset.wvolume;
    const sound = el.dataset.wsound;
    const id = el.dataset.wid;
    const danger = el.dataset.wdanger
    const type = el.dataset.wtype;
    const label = el.dataset.wlabel;
    if (!volume || !sound || !id || !danger || !type || !label) {
        return null;
    }
    return {
        volume: parseFloat(volume),
        danger: parseInt(danger),
        type: type as WWampType,
        sound,
        id,
        label,
    }
}

const components = {
    getChooseFileLabel(): HTMLButtonElement {
        const el = document.getElementById('choose-file-label');
        if (!el) {
            throw new Error('Choose file label not found');
        }
        return el as HTMLButtonElement;
    },
    displayUpload(): void {
        const el = document.getElementById('upload-input-container');
        if (el instanceof HTMLElement) {
            el.style.display = 'block';
        }
    },
    getDefaultWwampButtons(): HTMLElement[] {
        const defaults = document.querySelectorAll('.default-wwamp');
        return Array.from(defaults) as HTMLElement[];
    },
    getCustomWWampButtons(): HTMLElement[] {
        const defaults = document.querySelectorAll('.custom-wwamp');
        return Array.from(defaults) as HTMLElement[];
    },
    getDefaultWwamps(): WWamp[] {
        const defaults = components.getDefaultWwampButtons();
        const wwamps: WWamp[] = [];
        for (const el of defaults) {
            const wwamp = wwampFromElement(el);
            if (wwamp) {
                wwamps.push(wwamp);
            }
        }

        return wwamps;
    },
    getCustomWwamps(): WWamp[] {
        const defaults = components.getCustomWWampButtons();
        const wwamps: WWamp[] = [];
        for (const el of defaults) {
            const wwamp = wwampFromElement(el);
            if (wwamp) {
                wwamps.push(wwamp);
            }
        }

        return wwamps;
    },
    getUploadFileInput(): HTMLInputElement {
        const el = document.getElementById('file-input');
        if (!el) {
            throw new Error('File input not found');
        }
        return el as HTMLInputElement;
    },
    getUploadFileLabelInput(): HTMLInputElement {
        const el = document.getElementById('file-label');
        if (!el) {
            throw new Error('File label input not found');
        }
        return el as HTMLInputElement;
    },
    getUploadBtn(): HTMLButtonElement {
        const el = document.getElementById('upload-btn');
        if (!el) {
            throw new Error('Upload button not found');
        }
        return el as HTMLButtonElement;
    },
    getWwampContainer(): HTMLElement {
        const el = document.getElementById('wwamp-container');
        if (!el) {
            throw new Error('Wwamp container not found');
        }
        return el;
    },
    getCustomWWampContainer(): HTMLElement {
        const el = document.getElementById('custom-wwamp-container');
        if (!el) {
            throw new Error('Custom wwamp container not found');
        }
        return el;
    },
    getVolumeCtl(): HTMLInputElement {
        const el = document.getElementById('volume');
        if (!el) {
            throw new Error('Volume control not found');
        }
        return el as HTMLInputElement;
    },
    getSoundSplash(): HTMLElement {
        const el = document.getElementById('sound-splash');
        if (!el) {
            throw new Error('Sound splash not found');
        }
        return el;
    },
    getAccountNameInput(): HTMLInputElement {
        const el = document.getElementById('account');
        if (!el) {
            throw new Error('Account name not found');
        }
        return el as HTMLInputElement;
    },
    getEphemeralBubbles(): HTMLElement {
        const el = document.getElementById('ephermal-bubbles');
        if (!el) {
            throw new Error('Ephemeral bubbles not found');
        }
        return el;
    },
    getStatus(): HTMLElement {
        const el = document.getElementById('status');
        if (!el) {
            throw new Error('Status not found');
        }
        return el;
    },
    getUserList(): HTMLElement {
        const el = document.getElementById('users');
        if (!el) {
            throw new Error('User list not found');
        }
        return el;
    },
    getAccountNameDisplay(): HTMLElement {
        const el = document.getElementById('account-display');
        if (!el) {
            throw new Error('Account name display not found');
        }
        return el;
    }
};

type Components = typeof components;

type HowlerService = {
    volume: (volume: number, id?: number) => void;
    state: string;
}

type HowlInstance = {
    play: (sound?: string) => number;
    volume: (volume: number, id?: number) => void;
}

type SocketIO = {
    emit: (event: string, data: any) => void;
    on: (event: string, callback: (data: any) => void) => void;
}

const Services = {
    getHowler(): HowlerService {
        // @ts-ignore
        return Howler;
    },
    newHowl(sprites: Sprites): HowlInstance {
        // @ts-ignore
        return new Howl(sprites);
    },
    newSound(url: string): HowlInstance {
        // @ts-ignore
        return new Howl({
            src: [url],
            urls: [url],
            volume: 0.75,
        });
    },
    getIO(): SocketIO {
        // @ts-ignore
        return io();
    }
}

type Event = {
    who: string;
    what: number;
    sound: string;
}

const App = {
    play(howl: HowlInstance, sound: string, volume: number) {
        const id = howl.play(sound);
        howl.volume(volume, id);
    },
    show(wwamp: WWamp, who: string) {
        const container = components.getEphemeralBubbles();
        let rand = Math.random() * 100;
        const hpos = rand < 50 ? `left: ${rand}%;` : `right: ${100 - rand}%;`;
        const animate_ms = 2500 + 1000 * Math.random(); // 2.5 - 3.5s

        const bubble = htmlToElement<HTMLElement>(
            '<div class="message db tc f3 br-pill ph4 pv3 white bg-' + dangerColors[wwamp.danger] + '"' +
            'style="position: fixed; ' + hpos + ';' +
            'animation-name: bubble;' +
            'animation-duration: ' + animate_ms + 'ms;' +
            'animation-timing-function: linear;' +
            'animation-fill-mode: forwards;' +
            'pointer-events: none;">' +
            '<span style="text-shadow:2px 2px 3px rgba(0,0,0,.3);margin-right:3px;"></span> <span class="nowrap"></span>' +
            '</div>');
            // @ts-ignore
        bubble.firstChild.innerText = who;
        // @ts-ignore
        bubble.lastChild.innerText = wwamp.label;

        container.appendChild(bubble);
        while (container.childElementCount > 50 && container.firstChild) {
            container.removeChild(container.firstChild);
        }
        window.setTimeout(function () {
            container.removeChild(bubble)
        }, animate_ms);
    },
    getAccountName() {
        return components.getAccountNameInput().value || 'anonymous';
    },
    send(socket: SocketIO, wwamp: WWamp) {
        socket.emit('sound', {
            who: this.getAccountName(),
            what: 2,
            sound: wwamp.sound,
        });
    },
    open() {
        const state = components.getStatus();
        state.className = "green";
        state.innerText = "connected";
    },
    sound(howl: HowlInstance, soundboard: Soundboard, wwamps: WWamp[], event: Event) {
        const wwamp = wwamps.find(w => w.sound === event.sound);
        if (!wwamp) {
            console.error("no such wwamp", {event});
            return;
        }

        this.show(wwamp, event.who);

        if (wwamp.type === WWampTypes.default) {
            this.play(howl, wwamp.sound, wwamp.volume);
            return;
        }

        if (event.who === this.getAccountName()) {
            return;
        }

        if (wwamp.type === WWampTypes.custom) {
            const audio = soundboard[wwamp.id];
            if (!audio) {
                console.error(event.who, "no such custom audio", {event, wwamp})
                return;
            }
            audio.play();
            return;
        }
        console.error('Unknown wwamp type provided', wwamp);
    },
    reloadPage() {
        window.location.reload();
    },
    error() {
        const state = components.getStatus();
        state.className = "red";
        state.innerText = "error";
    },
    removeSound(wwamps: WWamp[], soundboard: Soundboard, evt: { wwamp: WWamp }) {
        const wwamp = evt.wwamp;
        const index = wwamps.indexOf(wwamp);
        if (index !== -1) {
            wwamps.splice(index, 1);
        }
        const el = getWwampElement(wwamp);
        if (el) {
            el.remove();
        }
        const audio = soundboard[wwamp.id];
        if (audio) {
            // @ts-ignore
            audio.stop();
            delete soundboard[wwamp.id];
        }
    }
}

function createWwampElement(wwamp: WWamp, container: HTMLElement): HTMLElement {
    const id = toElementId(wwamp);
    const inner = htmlToElement<HTMLElement>(`
        <div style="user-select: none; white-space: nowrap; cursor: pointer" class="db tc f3 link br-pill ba bw2 ph2 pv2 pv3-ns mb3 mb2-ns hover-bg-moon-gray dark-green bg-white"></div>
    `);
    inner.id = id;
    inner.title = wwamp.label;
    inner.classList.add(`${dangerColors[wwamp.danger]}`);
    inner.textContent = wwamp.label;
    const outer = htmlToElement<HTMLElement>(`<div class="pv1 wwamp wwamp-item default-wwamp" style="width:48%" style="width:48%"></div>`);
    outer.appendChild(inner);
    container.appendChild(outer);
    return outer;
}

function upsertWwampElement(socket: SocketIO, wwamp: WWamp, container: HTMLElement) {
    let input = getWwampElement(wwamp);
    if (!input) {
        input = createWwampElement(wwamp, container);
        input.dataset.wsound = wwamp.sound;
        input.dataset.wid = wwamp.id;
        input.dataset.wvolume = wwamp.volume.toString();
        input.dataset.wdanger = wwamp.danger.toString();
        input.dataset.wtype = wwamp.type;
        input.dataset.wlabel = wwamp.label;
    }
    input.addEventListener('click', (e) => {
        App.send(socket, wwamp);
        e.preventDefault();
    });
}

function setupUI(socket: SocketIO, wwamps: WWamp[]) {
    for (const wwamp of wwamps) {
        upsertWwampElement(socket, wwamp, components.getCustomWWampContainer());
    }
}

function setupDefaultAudio(sprites: Sprites) {
    const howl = Services.newHowl(sprites);

    function tryStartAudio(howl: HowlInstance, soundSplash: HTMLElement, click: boolean) {
        const howler = Services.getHowler();
        howler.volume(0);
        if (howler.state == "suspended") {
            soundSplash.style.display = 'flex';
        }
        if (howler.state == "running" || click) {
            updateVolume(localStorage.getItem("volume") ?? '0.5');
            if (click) {
                App.play(howl, "power-on", 0.3);
            }
            soundSplash.style.display = 'none';
        }
    }

    function updateVolume(currentVolume: string) {
        let vol = 0.5;
        if (currentVolume) {
            const v = parseFloat(currentVolume);
            if (v >= 0 && v <= 1.0) {
                vol = v;
            }
        }
        // components.getVolumeCtl().value = vol.toString();
        localStorage.setItem("volume", vol.toString());
        Services.getHowler().volume(vol * vol);
    }

    const soundSplash = components.getSoundSplash();
    soundSplash.addEventListener('click', () => tryStartAudio(howl, soundSplash, true));
    // const volumeCtl = components.getVolumeCtl();
    // volumeCtl.addEventListener('input', (ev) => updateVolume(volumeCtl.value));

    tryStartAudio(howl, soundSplash, false);

    return howl;
}

function addClickSound(el: HTMLElement, audio: HowlInstance, wwamp: WWamp) {
    el.addEventListener("click", () => {
        audio.play();
    });
}

function setupCustomWwampAudio(wwamp: WWamp, audio: HowlInstance) {
    if (wwamp.type !== WWampTypes.custom) {
        return;
    }
    const el = getWwampElement(wwamp);
    if (el === null) {
        console.error('Unable to locate expected element', wwamp);
        return;
    }
    addClickSound(el, audio, wwamp);
}

type Soundboard = Record<string, HowlInstance>;

function setupCustomSoundboard(wwamps: WWamp[]): Soundboard {
    const soundboard: Soundboard = {};
    for (const wwamp of wwamps) {
        if (wwamp.type !== WWampTypes.custom) {
            continue;
        }
        const audio = Services.newSound(wwamp.sound);
        setupCustomWwampAudio(wwamp, audio);
        soundboard[wwamp.id] = audio;
    }
    return soundboard;
}

function setupSocket(howl: HowlInstance, soundboard: Soundboard, wwamps: WWamp[]) {
    const socket = Services.getIO();
    socket.on("connect", () => App.open());
    socket.on("sound", (evt) => App.sound(howl, soundboard, wwamps, evt));
    socket.on("error", () => App.error())
    socket.on("remove-sound", (evt) => App.removeSound(wwamps, soundboard, evt));
}

function setupDisplay(socket: SocketIO) {
    const accountNameInput = components.getAccountNameInput();
    const accountNameDisplay = components.getAccountNameDisplay();
    const storedAccount = localStorage.getItem('accountName');
    socket.emit('account', {
        value: storedAccount,
    });
    if (storedAccount) {
        accountNameInput.value = storedAccount;
        accountNameDisplay.innerText = storedAccount;
    }
    accountNameInput.addEventListener('input', () => {
        localStorage.setItem('accountName', accountNameInput.value);
        accountNameDisplay.innerText = accountNameInput.value;
        socket.emit('account', {
            value: accountNameInput.value,
        })
    });
}

type HandleUploadOptions = {
    socket: SocketIO;
    fileNameInput: HTMLInputElement;
    fileInput: HTMLInputElement;
    statusEl: HTMLElement;
}

async function handleUpload(options: HandleUploadOptions) {
    const {statusEl, fileInput, fileNameInput} = options;
    try {
        statusEl.textContent = 'Uploading...';
        if (!fileInput.files?.length) {
            throw new Error('Please select a file to upload.');
        }

        const file = fileInput.files[0];
        if (!file) {
            throw new Error('No file selected.');
        }

        const label = fileNameInput.value.trim();
        if (label.length === 0) {
            throw new Error('Please provide a label for the file.');
        }

        const { url } = await uploadFile({
            file,
            label,
            endpoint: '/api/upload',
            baseUrl: window.location.origin
        });

        statusEl.textContent = `Uploaded! File URL: ${url}`;
    } catch (err: any) {
        console.error(err);
        statusEl.textContent = `Error: ${err.message}`;
    }
}

type UploadFileOptions = {
    file: File;
    label: string;
    endpoint: string;
    baseUrl?: string;
}

async function uploadFile({file, label, endpoint, baseUrl = ''}: UploadFileOptions) {
    if (!endpoint) throw new Error('uploadFile: "endpoint" is required');
    if (!file) throw new Error('uploadFile: no file provided');

    const formData = new FormData();
    formData.append('sound', file);
    formData.append('label', label);

    const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
    });

    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Upload failed (${response.status}): ${text || 'no response body'}`);
    }

    const json = await response.json();
    const path = json?.data?.path;
    if (!path) throw new Error('Upload succeeded but response did not include data.path');

    const url = baseUrl ? new URL(path, baseUrl).toString() : path;
    return {path, url};
}

function setupUpload(socket: SocketIO, components: Components) {
    const fileInput = components.getUploadFileInput();
    const fileNameInput = components.getUploadFileLabelInput();
    const uploadBtn = components.getUploadBtn();
    const statusEl = components.getStatus();
    const chooseFileLabel = components.getChooseFileLabel();

    function updateChooseFileLabel() {
        if (fileInput.files && fileInput.files.length > 0) {
            chooseFileLabel.textContent = 'Selected';
        } else {
            chooseFileLabel.textContent = 'Choose File';
        }
    }

    function updateUploadButtonState(hasError: boolean = false) {
        const hasFile = fileInput.files && fileInput.files.length > 0;
        const hasName = fileNameInput.value.trim().length > 1;

        uploadBtn.disabled = hasError || !(hasFile && hasName);
    }

    updateUploadButtonState();

    uploadBtn.addEventListener('click', async (e) => {
        await handleUpload({
            socket,
            statusEl,
            fileInput,
            fileNameInput
        }).then(() => {
            // clear the file input
            fileInput.value = '';
            fileNameInput.value = '';
            updateUploadButtonState();
            updateChooseFileLabel();
        }).catch((err: any) => {
            console.error('error uploading', {err});
            statusEl.textContent = `Error: ${err.message}`;
            updateUploadButtonState(true);
        });
    });

    fileNameInput.addEventListener('input', () => {
        if (fileNameInput.validity.patternMismatch) {
            fileNameInput.setCustomValidity("Only emoji labels are supported. Some are not currently supported 😭");
            fileNameInput.reportValidity();
        } else {
            fileNameInput.setCustomValidity("");
        }
        updateUploadButtonState();
        updateChooseFileLabel();
    })

    fileInput.addEventListener("change", () => {
        updateUploadButtonState();
        updateChooseFileLabel();
    });

    socket.on('upload', (event) => {
        if (event.enable) {
            components.displayUpload();
        }
    });
}

(function main() {
    const io = Services.getIO();
    const defaultWwamps = components.getDefaultWwamps();
    const customWWamps = components.getCustomWwamps();
    const wwamps = [
        ...defaultWwamps,
        ...customWWamps,
    ];
    const howl = setupDefaultAudio(defaultSprites);
    const soundboard = setupCustomSoundboard(customWWamps);
    // @ts-ignore
    this.window.soundboard = soundboard;
    setupUI(io, wwamps);
    setupSocket(howl, soundboard, wwamps);
    setupDisplay(io);
    setupUpload(io, components);
    io.on('new-sound', (evt) => {
        console.log('custom-wwamp', {evt});
        const wwamp = evt.wwamp;
        wwamps.push(wwamp);
        const audio = Services.newSound(wwamp.sound);
        soundboard[wwamp.id] = audio;
        upsertWwampElement(io, wwamp, components.getCustomWWampContainer());
        setupCustomWwampAudio(wwamp, audio);
    });
})();